
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, CheckCircle } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import OrganizationLogo from '../components/OrganizationLogo';
import { supabase } from '@/integrations/supabase/client';

const ForgotPassword: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [error, setError] = useState('');
  const [organizationName, setOrganizationName] = useState('Growth Energy');
  const [organizationLogo, setOrganizationLogo] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrganization = async () => {
      const orgId = localStorage.getItem('organizationId');
      if (orgId) {
        const { data, error } = await supabase
          .from('organizations')
          .select('name, logo_url')
          .eq('id', orgId)
          .single();

        if (!error && data) {
          setOrganizationName(data.name);
          setOrganizationLogo(data.logo_url);
        }
      }
    };

    fetchOrganization();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!email) {
      setError(t('auth.enterEmailError'));
      setIsLoading(false);
      return;
    }

    try {
      // Get redirect URL from environment or use current origin
      const redirectUrl = `${window.location.origin}/reset-password`;

      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });

      if (resetError) {
        throw resetError;
      }

      setIsEmailSent(true);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(err.message || t('auth.failedSendReset'));
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-6 py-12 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                {t('auth.emailSent')}
              </h2>

              <p className="text-gray-600 mb-6">
                {t('auth.resetLinkSentTo', { email })}
              </p>

              <div className="space-y-4">
                <Link
                  to="/login"
                  className="w-full bg-blue-500 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-600 transition-colors inline-block text-center"
                >
                  {t('auth.backToLogin')}
                </Link>

                <button
                  onClick={() => {
                    setIsEmailSent(false);
                    setEmail('');
                  }}
                  className="w-full text-blue-500 py-2 px-4 font-medium hover:text-blue-600 transition-colors"
                >
                  {t('auth.resendEmail')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-6 py-12 bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <OrganizationLogo
              logoUrl={organizationLogo}
              organizationName={organizationName}
              size="md"
            />
          </div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            {t('auth.forgotPasswordTitle')}
          </h2>
          <p className="text-gray-600">
            {t('auth.forgotPasswordSubtitle')}
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg animate-fade-in">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                {t('auth.emailAddress')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={t('auth.emailPlaceholder')}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-500 hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <LoadingSpinner size="sm" />
                  <span>{t('auth.sending')}</span>
                </div>
              ) : (
                t('auth.sendResetLink')
              )}
            </button>
          </form>

          <div className="mt-6">
            <Link
              to="/login"
              className="flex items-center justify-center space-x-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft size={16} />
              <span>{t('auth.backToLogin')}</span>
            </Link>
          </div>
        </div>

        {/* Help */}
        <div className="mt-8 text-center">
          <p className="text-sm text-gray-600">
            {t('auth.needHelp')} {' '}
            <a href="#" className="font-medium text-blue-500 hover:text-blue-600 transition-colors">
              {t('auth.contactSupport')}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
